import React, { useState, useEffect } from 'react';
import './PhiConfigModal.css';

const ANON_TYPES = [
  { value: 0, label: 'Name Mask', description: 'Bill Smith → B*** S****' },
  { value: 1, label: 'Date Mask', description: '03/15/2000 → **/**/2000' },
  { value: 2, label: 'Full Mask', description: 'Value → ****' }
];

function PhiConfigModal({ onClose, getPhiConfig, updatePhiConfig, deletePhiConfig }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newField, setNewField] = useState('');
  const [newType, setNewType] = useState(0);
  const [newDataTypes, setNewDataTypes] = useState('*');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const data = await getPhiConfig();
      setConfigs(data.configs || []);
    } catch (err) {
      setError('Failed to load PHI configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (config) => {
    const updated = { ...config, enabled: !config.enabled };
    try {
      await updatePhiConfig([updated]);
      setConfigs(prev => prev.map(c => c.id === config.id ? { ...c, enabled: !c.enabled } : c));
    } catch {
      setError('Failed to update config');
    }
  };

  const handleAdd = async () => {
    if (!newField.trim()) return;
    try {
      setSaving(true);
      await updatePhiConfig([{
        id: 0,
        fieldPath: newField.trim(),
        anonymizationType: newType,
        dataTypes: newDataTypes || '*',
        enabled: true
      }]);
      setNewField('');
      setNewType(0);
      setNewDataTypes('*');
      await loadConfigs();
    } catch {
      setError('Failed to add config');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePhiConfig(id);
      setConfigs(prev => prev.filter(c => c.id !== id));
    } catch {
      setError('Failed to delete config');
    }
  };

  const getAnonLabel = (type) => {
    return ANON_TYPES.find(t => t.value === type)?.label || 'Unknown';
  };

  return (
    <div className="phi-modal-overlay" onClick={onClose}>
      <div className="phi-modal" onClick={(e) => e.stopPropagation()}>
        <div className="phi-modal-header">
          <h2>PHI Anonymization Settings</h2>
          <button className="phi-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="phi-modal-body">
          <p className="phi-description">
            Configure which fields are anonymized when data is stored in the local cache.
            Changes apply to newly refreshed data. Use Full Refresh of Range
            (or Wipe) to re-anonymize rows already in the cache.
          </p>

          {error && <div className="phi-error">{error}</div>}

          {loading ? (
            <div className="phi-loading">Loading configuration...</div>
          ) : (
            <>
              <table className="phi-config-table">
                <thead>
                  <tr>
                    <th>Enabled</th>
                    <th>Field Path</th>
                    <th>Mask Type</th>
                    <th>Data Types</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map(config => (
                    <tr key={config.id} className={config.enabled ? '' : 'disabled'}>
                      <td>
                        <input
                          type="checkbox"
                          checked={config.enabled}
                          onChange={() => handleToggle(config)}
                        />
                      </td>
                      <td><code>{config.fieldPath}</code></td>
                      <td>{getAnonLabel(config.anonymizationType)}</td>
                      <td><code>{config.dataTypes}</code></td>
                      <td>
                        <button className="phi-delete-btn" onClick={() => handleDelete(config.id)} title="Delete">×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="phi-add-form">
                <h4>Add New Rule</h4>
                <div className="phi-add-row">
                  <input
                    type="text"
                    value={newField}
                    onChange={(e) => setNewField(e.target.value)}
                    placeholder="Field name (e.g. patient_name)"
                    className="phi-add-input"
                  />
                  <select value={newType} onChange={(e) => setNewType(parseInt(e.target.value))} className="phi-add-select">
                    {ANON_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label} — {t.description}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newDataTypes}
                    onChange={(e) => setNewDataTypes(e.target.value)}
                    placeholder="Data types (* = all)"
                    className="phi-add-input small"
                  />
                  <button className="phi-add-btn" onClick={handleAdd} disabled={saving || !newField.trim()}>
                    Add
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PhiConfigModal;
